import React from 'react';
import { DEFAULT_COMPANY_INFO, resolveCompanyLogoUrl } from '../../../utils/companyInfo';

const InspectionPrintSheet = ({ inspection, companyInfo = DEFAULT_COMPANY_INFO }) => {
  if (!inspection) return null;

  const companyName = companyInfo?.company_name || DEFAULT_COMPANY_INFO.company_name;
  const companyAddress = companyInfo?.company_address || '';
  const companyPhone = companyInfo?.company_phone || '';
  const companyEmail = companyInfo?.company_email || '';
  const logoUrl = resolveCompanyLogoUrl(companyInfo);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Format date short
  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'passed':
        return { text: '✓ PASSED', class: 'bg-green-100 text-green-800 border-green-300' };
      case 'failed':
        return { text: '✗ FAILED', class: 'bg-red-100 text-red-800 border-red-300' };
      case 'conditional':
        return { text: '⚠ CONDITIONAL', class: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
      case 'pending':
        return { text: '⏳ PENDING', class: 'bg-gray-100 text-gray-800 border-gray-300' };
      default:
        return { text: status || 'PENDING', class: 'bg-gray-100 text-gray-800 border-gray-300' };
    }
  };

  // Group check items by category
  const groupedItems = (inspection.check_items || []).reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {});

  const statusBadge = getStatusBadge(inspection.result);

  return (
    <div id="inspection-print-sheet" className="inspection-sheet" style={{ 
      fontFamily: 'Arial, sans-serif',
      padding: '20px',
      maxWidth: '210mm',
      margin: '0 auto',
      backgroundColor: '#fff',
      color: '#000'
    }}>
      {/* Header with Logo and Company Info */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '15px',
        borderBottom: '2px solid #FF5733',
        paddingBottom: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img 
            src={logoUrl} 
            alt="Company Logo" 
            crossOrigin="anonymous"
            style={{ 
              width: '50px', 
              height: '50px', 
              objectFit: 'contain'
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '22px', 
              fontWeight: 'bold',
              color: '#2a2e30',
              letterSpacing: '0.5px'
            }}>
              {companyName}
            </h1>
            <p style={{ 
              margin: '2px 0 0 0', 
              fontSize: '12px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              Vehicle Inspection Report
            </p>
            {(companyPhone || companyEmail || companyAddress) && (
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '11px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                {[companyPhone, companyEmail, companyAddress].filter(Boolean).join(' | ')}
              </p>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ 
            padding: '6px 12px',
            border: `2px solid ${statusBadge.class.includes('green') ? '#10b981' : statusBadge.class.includes('red') ? '#ef4444' : statusBadge.class.includes('yellow') ? '#f59e0b' : '#6b7280'}`,
            borderRadius: '6px',
            display: 'inline-block',
            fontWeight: 'bold',
            fontSize: '12px',
            ...(statusBadge.class.includes('green') ? { backgroundColor: '#d1fae5', color: '#065f46' } :
                statusBadge.class.includes('red') ? { backgroundColor: '#fee2e2', color: '#991b1b' } :
                statusBadge.class.includes('yellow') ? { backgroundColor: '#fef3c7', color: '#92400e' } :
                { backgroundColor: '#f3f4f6', color: '#374151' })
          }}>
            {statusBadge.text}
          </div>
        </div>
      </div>

      {/* Inspection Type Badge */}
      <div style={{ 
        textAlign: 'center',
        marginBottom: '15px',
        padding: '5px',
        backgroundColor: '#EDE4DA',
        borderRadius: '4px'
      }}>
        <span style={{ 
          fontSize: '12px', 
          fontWeight: 'bold',
          color: '#2a2e30',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          Internal Inspection
        </span>
      </div>

      {/* Vehicle Information Section */}
      <div style={{ 
        marginBottom: '15px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '10px',
        backgroundColor: '#fff8f5'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '14px', 
          fontWeight: 'bold',
          color: '#2a2e30',
          borderBottom: '1px solid #FF5733',
          paddingBottom: '4px'
        }}>
          Vehicle Information
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '8px',
          fontSize: '11px'
        }}>
          <div>
            <strong style={{ color: '#666' }}>Plate Number:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30', fontWeight: 'bold' }}>
              {inspection.plate_no || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Make:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.make || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Model:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.model || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Year:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.year || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Driver:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.driver_name || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Owner:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.owner_name || inspection.owner_code || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Mileage:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.mileage ? `${inspection.mileage.toLocaleString()} km` : '-'}
            </span>
          </div>
        </div>
      </div>

      {/* Check Items Section */}
      {inspection.check_items && inspection.check_items.length > 0 && (
        <div style={{ marginBottom: '25px' }}>
          <h2 style={{ 
            margin: '0 0 15px 0', 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#2a2e30', 
            borderBottom: '2px solid #FF5733',
            paddingBottom: '8px'
          }}>
            Inspection Check Items
          </h2>
          
          {Object.entries(groupedItems).map(([category, items]) => (
            <div key={category} style={{ marginBottom: '20px' }}>
              <h3 style={{ 
                margin: '0 0 10px 0', 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: '#FF5733',
                backgroundColor: '#EDE4DA',
                padding: '8px 12px',
                borderRadius: '4px'
              }}>
                {category}
              </h3>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                marginBottom: '15px',
                fontSize: '12px',
                tableLayout: 'fixed'
              }}>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '30%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '50%' }} />
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: '#f3f4f6' }}>
                    <th style={{ 
                      border: '1px solid #d1d5db', 
                      padding: '10px', 
                      textAlign: 'left',
                      fontWeight: 'bold',
                      color: '#2a2e30'
                    }}>
                      Code
                    </th>
                    <th style={{ 
                      border: '1px solid #d1d5db', 
                      padding: '10px', 
                      textAlign: 'left',
                      fontWeight: 'bold',
                      color: '#2a2e30'
                    }}>
                      Item Name
                    </th>
                    <th style={{ 
                      border: '1px solid #d1d5db', 
                      padding: '10px', 
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: '#2a2e30'
                    }}>
                      Status
                    </th>
                    <th style={{ 
                      border: '1px solid #d1d5db', 
                      padding: '10px', 
                      textAlign: 'left',
                      fontWeight: 'bold',
                      color: '#2a2e30'
                    }}>
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const itemStatus = item.status?.toLowerCase();
                    const statusSymbol = itemStatus === 'passed' ? '✓' : 
                                        itemStatus === 'failed' ? '✗' : 
                                        itemStatus === 'needs_attention' ? '⚠' : '○';
                    const statusColor = itemStatus === 'passed' ? '#10b981' : 
                                      itemStatus === 'failed' ? '#ef4444' : 
                                      itemStatus === 'needs_attention' ? '#f59e0b' : '#6b7280';
                    
                    // Format status text for display
                    const formatStatus = (status) => {
                      if (!status) return 'Not Checked';
                      const statusLower = status.toLowerCase();
                      switch (statusLower) {
                        case 'passed':
                          return 'Passed';
                        case 'failed':
                          return 'Failed';
                        case 'needs_attention':
                          return 'Needs Attention';
                        case 'not_checked':
                          return 'Not Checked';
                        default:
                          return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
                      }
                    };
                    
                    return (
                      <tr key={item.id || idx} style={{ 
                        backgroundColor: idx % 2 === 0 ? '#fff' : '#fff8f5'
                      }}>
                        <td style={{ 
                          border: '1px solid #d1d5db', 
                          padding: '8px',
                          fontWeight: 'bold',
                          color: '#2a2e30',
                          verticalAlign: 'top'
                        }}>
                          {item.item_code || '-'}
                        </td>
                        <td style={{ 
                          border: '1px solid #d1d5db', 
                          padding: '8px',
                          color: '#2a2e30',
                          verticalAlign: 'top'
                        }}>
                          <div style={{ fontWeight: 600 }}>{item.item_name || '-'}</div>
                          {item.description && (
                            <div style={{
                              marginTop: '6px',
                              fontSize: '11px',
                              color: '#6b7280',
                              fontStyle: 'italic',
                              lineHeight: '1.3'
                            }}>
                              {item.description}
                            </div>
                          )}
                        </td>
                        <td style={{ 
                          border: '1px solid #d1d5db', 
                          padding: '8px',
                          textAlign: 'center',
                          fontWeight: 'bold',
                          color: statusColor,
                          verticalAlign: 'top'
                        }}>
                          {statusSymbol} {formatStatus(item.status)}
                        </td>
                        <td style={{ 
                          border: '1px solid #d1d5db', 
                          padding: '8px',
                          color: '#666',
                          fontSize: '12px',
                          verticalAlign: 'top',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {(item.status && item.status.toLowerCase() !== 'not_checked') ? (item.notes || '-') : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Inspection Details Section - Moved to bottom */}
      <div style={{ 
        marginBottom: '15px',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        padding: '10px',
        backgroundColor: '#fff'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '14px', 
          fontWeight: 'bold',
          color: '#2a2e30',
          borderBottom: '1px solid #FF5733',
          paddingBottom: '4px'
        }}>
          Inspection Details
        </h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '8px',
          fontSize: '11px'
        }}>
          <div>
            <strong style={{ color: '#666' }}>Date:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {formatDate(inspection.inspection_date)}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Next Due:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {formatDate(inspection.next_inspection_due)}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Inspector:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.inspector_name || '-'}
            </span>
          </div>
          <div>
            <strong style={{ color: '#666' }}>Org:</strong>
            <span style={{ marginLeft: '5px', color: '#2a2e30' }}>
              {inspection.inspector_organization || 'Cosmo Fleets'}
            </span>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      {inspection.notes && (
        <div style={{ 
          marginBottom: '25px',
          border: '2px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          backgroundColor: '#fff8f5'
        }}>
          <h2 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '18px', 
            fontWeight: 'bold',
            color: '#2a2e30',
            borderBottom: '2px solid #FF5733',
            paddingBottom: '8px'
          }}>
            General Notes
          </h2>
          <p style={{ 
            margin: 0, 
            fontSize: '14px', 
            color: '#2a2e30',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap'
          }}>
            {inspection.notes}
          </p>
        </div>
      )}

      {/* Overall Notes */}
      {inspection.overall_notes && (
          <div style={{
              marginBottom: '25px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#fff8f5'
          }}>
              <h2 style={{
                  margin: '0 0 10px 0',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#2a2e30',
                  borderBottom: '2px solid #FF5733',
                  paddingBottom: '8px'
              }}>
                  Overall Notes
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#2a2e30', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {inspection.overall_notes}
              </p>
          </div>
      )}

      {/* Recommendations */}
      {inspection.recommendations && (
          <div style={{
              marginBottom: '25px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: '#fff8f5'
          }}>
              <h2 style={{
                  margin: '0 0 10px 0',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#2a2e30',
                  borderBottom: '2px solid #FF5733',
                  paddingBottom: '8px'
              }}>
                  Recommendations
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#2a2e30', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                  {inspection.recommendations}
              </p>
          </div>
      )}

      {/* General Photos */}
      {inspection.metadata?.photos?.length > 0 && (
          <div style={{ marginBottom: '25px' }}>
              <h2 style={{
                  margin: '0 0 15px 0',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#2a2e30',
                  borderBottom: '2px solid #FF5733',
                  paddingBottom: '8px'
              }}>
                  Inspection Photos
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {inspection.metadata.photos.map((photo, idx) => (
                      <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                          <img
                              src={photo.url}
                              alt={photo.name || `Photo ${idx + 1}`}
                              crossOrigin="anonymous"
                              style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                          />
                          {photo.name && (
                              <p style={{ margin: '4px 8px', fontSize: '10px', color: '#666' }}>{photo.name}</p>
                          )}
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* Signature Section */}
      <div style={{ 
        marginTop: '40px',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        borderTop: '2px solid #e5e7eb',
        paddingTop: '30px'
      }}>
        <div>
          <div style={{ 
            borderBottom: '2px solid #2a2e30',
            marginBottom: '10px',
            height: '50px'
          }}></div>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: '#2a2e30'
          }}>
            Inspector Signature
          </p>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '12px', 
            color: '#666'
          }}>
            {inspection.inspector_name || 'Inspector Name'}
          </p>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '12px', 
            color: '#666'
          }}>
            Date: {formatDateShort(inspection.inspection_date)}
          </p>
        </div>
        <div>
          <div style={{ 
            borderBottom: '2px solid #2a2e30',
            marginBottom: '10px',
            height: '50px'
          }}></div>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '14px', 
            fontWeight: 'bold',
            color: '#2a2e30'
          }}>
            Authorized Signatory
          </p>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '12px', 
            color: '#666'
          }}>
            {companyName}
          </p>
          <p style={{ 
            margin: '5px 0 0 0', 
            fontSize: '12px', 
            color: '#666'
          }}>
            Date: {formatDateShort(new Date())}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div style={{ 
        marginTop: '40px',
        paddingTop: '20px',
        borderTop: '1px solid #e5e7eb',
        textAlign: 'center',
        fontSize: '11px',
        color: '#666'
      }}>
        <p style={{ margin: '5px 0' }}>
          <strong>{companyName}</strong> - Vehicle Inspection Report
        </p>
        <p style={{ margin: '5px 0' }}>
          Generated on {formatDate(new Date())} | Report ID: {inspection.id || 'N/A'}
        </p>
        <p style={{ margin: '5px 0', fontStyle: 'italic' }}>
          This is a computer-generated document. No signature is required for electronic copies.
        </p>
      </div>

      {/* Tear-off Slip */}
      <div style={{
        marginTop: '40px',
        borderTop: '2px dashed #000',
        paddingTop: '20px',
        position: 'relative'
      }}>
        {/* Scissors Icon */}
        <div style={{
          position: 'absolute',
          top: '-12px',
          right: '20px',
          backgroundColor: '#fff',
          padding: '0 10px',
          fontSize: '20px'
        }}>
          ✂️
        </div>

        <div style={{
          textAlign: 'center',
          fontFamily: 'monospace',
          fontSize: '16px'
        }}>
          <h3 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '20px', 
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            {companyName}
          </h3>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            padding: '10px',
            border: '2px solid #000',
            display: 'inline-block',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9'
          }}>
            NEXT INSPECTION DUE: {formatDate(inspection.next_inspection_due).toUpperCase()}
          </div>
          <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
            Plate: <strong>{inspection.plate_no}</strong> | Status: <strong>{statusBadge.text}</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default InspectionPrintSheet;

